import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../../components/Sidebar";
import BottomNav from "../../components/BottomNav";

import CountryStep from "../../components/user-transactions/CountryStep";
import AmountStep from "../../components/user-transactions/AmountStep";
import SenderAndBeneficiaryStep from "../../components/user-transactions/SenderAndBeneficiaryStep";
import TransactionSummary from "../../components/user-transactions/TransactionSummary";
import Processing from "../../components/user-transactions/Processing";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import StripePaymentForm from "../../components/user-transactions/StripePaymentForm";

import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../utils/axiosInstance";
import { useAuth } from "../../context/AuthContext";

const SERVED_AFRICA_ISO2 = new Set([
  "BJ",
  "BF",
  "CM",
  "CF",
  "TD",
  "CG",
  "CD",
  "CI",
  "GA",
  "GH",
  "GW",
  "GQ",
  "ML",
  "NE",
  "NG",
  "SN",
  "TG",
]);

function normalizeCountryCode(v) {
  if (!v) return "";
  const s = String(v).trim();

  if (/^[A-Za-z]{2}$/.test(s)) return s.toUpperCase();

  const name = s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const map = {
    cameroun: "CM",
    cameroon: "CM",
    canada: "CA",
    nigeria: "NG",
    ghana: "GH",
    rdc: "CD",
    "republique democratique du congo": "CD",
    "republique centrafricaine": "CF",
    rca: "CF",
    tchad: "TD",
    gabon: "GA",
    "guinee equatoriale": "GQ",
    benin: "BJ",
    "burkina faso": "BF",
    "cote d'ivoire": "CI",
    "guinee-bissau": "GW",
    mali: "ML",
    niger: "NE",
    senegal: "SN",
    togo: "TG",
    "congo-brazzaville": "CG",
  };

  return map[name] || "";
}

function isAfricanCountry(country) {
  const code = normalizeCountryCode(country);
  return SERVED_AFRICA_ISO2.has(code);
}

function isCanada(country) {
  return normalizeCountryCode(country) === "CA";
}

function initialFormData() {
  return {
    type: "transfer",

    sender: {
      firstName: "",
      lastName: "",
      fullName: "",
      phone: "",
      email: "",
      country: "",

      streetAddress: "",
      city: "",
      province: "",
      postalCode: "",

      address: "",
    },

    beneficiary: {
      id: "",
      firstName: "",
      lastName: "",
      fullName: "",
      phone: "",
      email: "",

      operator: "",
      bankName: "",
      accountNumber: "",
      interacEmail: "",

      streetAddress: "",
      city: "",
      province: "",
      postalCode: "",
    },

    mode: "SEND",
    currency: "",
    recipientCurrency: "",
    countryFrom: "",
    countryTo: "",
    method: "",
    calculation: null,
    amountToSend: "",
    amountToReceive: "",
    note: "",
    paymentProvider: "MANUAL",
  };
}

function buildDraftKey({ currentUser, countryFrom, countryTo, method }) {
  const uid = currentUser?.id || currentUser?._id || "guest";
  const from = String(countryFrom || "NA").toUpperCase();
  const to = String(countryTo || "NA").toUpperCase();
  const m = String(method || "NA").toUpperCase();
  return `transfer_sender_beneficiary:${uid}:${from}:${to}:${m}`;
}

export default function TransactionPage() {
  const { user: currentUser } = useAuth();

  const navigate = useNavigate();
  const [kycRequired, setKycRequired] = useState(null);

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(() => initialFormData());

  const [transactionData, setTransactionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState("");

  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const location = useLocation();

  useEffect(() => {
    const repeatTx = location?.state?.repeatTx;
    if (!repeatTx) return;

    setFormData(() => {
      const next = initialFormData();

      next.countryTo = repeatTx.countryTo || repeatTx.destinationCountry || "";
      next.method = repeatTx.method || "";
      next.currency = repeatTx.currency || "";
      next.recipientCurrency = repeatTx.recipientCurrency || "";

      const userCountry =
        currentUser?.profile?.countryCode ||
        currentUser?.UserProfile?.countryCode ||
        currentUser?.countryCode ||
        "";

      next.countryFrom = userCountry;

      next.sender = {
        ...next.sender,
        country: userCountry,
      };

      next.beneficiary = {
        ...next.beneficiary,
        ...(repeatTx.beneficiary || {}),
      };

      return next;
    });

    setStep(2);
  }, [location?.state?.repeatTx, currentUser]);

  const direction = useMemo(() => {
    const from = normalizeCountryCode(
      formData.countryFrom || formData.sender?.country || "",
    );
    const to = normalizeCountryCode(formData.countryTo || "");

    return {
      from,
      to,
      isAfricaToCanada: isAfricanCountry(from) && isCanada(to),
      isCanadaToAfrica: isCanada(from) && isAfricanCountry(to),
      isCanadaToNigeria: from === "CA" && to === "NG",
      isNigeriaToCanada: from === "NG" && to === "CA",
      isCanadaToRDC: from === "CA" && to === "CD",
      isCanadaToGhana: from === "CA" && to === "GH",
      isAfricaToAfrica:
        isAfricanCountry(from) && isAfricanCountry(to) && from !== to,
    };
  }, [formData.countryFrom, formData.countryTo, formData.sender?.country]);

  const draftKey = useMemo(() => {
    return buildDraftKey({
      currentUser,
      countryFrom: formData.countryFrom,
      countryTo: formData.countryTo,
      method: formData.method,
    });
  }, [currentUser, formData.countryFrom, formData.countryTo, formData.method]);

  const handleConfirm = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const calc = formData.calculation || {};
      const method = formData.method;

      const amtSend = Number(calc.amountToSend ?? 0);
      const amtReceive = Number(calc.convertedAmount ?? 0);

      const payload = {
        type: formData.type || "transfer",
        mode: formData.mode,
        paymentProvider: formData.paymentProvider || "MANUAL",

        amountToSend: amtSend,
        amountToReceive: amtReceive,

        currencyFrom: formData.currency || calc.currencyFrom || "CAD",
        recipientCurrency:
          formData.recipientCurrency || calc.recipientCurrency || "XAF",

        destinationCountry: formData.countryTo,
        method,

        sender: formData.sender,
        beneficiary: formData.beneficiary,
        countryFrom: formData.countryFrom,

        recipientFirstName: formData.beneficiary?.firstName || "",
        recipientLastName: formData.beneficiary?.lastName || "",
        recipientName:
          `${formData.beneficiary?.firstName || ""} ${formData.beneficiary?.lastName || ""}`.trim(),

        recipientEmail: formData.beneficiary?.email || null,
        recipientPhone: formData.beneficiary?.phone || null,

        mobileOperator:
          method === "MOBILE_MONEY"
            ? formData.beneficiary?.operator
            : undefined,
        bankName:
          method === "BANK_TRANSFER"
            ? formData.beneficiary?.bankName
            : undefined,
        accountNumber:
          method === "BANK_TRANSFER"
            ? formData.beneficiary?.accountNumber
            : undefined,
        interacEmail:
          method === "INTERAC" ? formData.beneficiary?.interacEmail : undefined,

        note: formData.note || "",

        calculation: formData.calculation
          ? {
              exchange_rate: calc.exchange_rate,
              amountToSend: calc.amountToSend,
              convertedAmount: calc.convertedAmount,
              fee: calc.fee,
              totalToPay: calc.totalToPay,
              currencyFrom: calc.currencyFrom,
              recipientCurrency: calc.recipientCurrency,
              feePercent: calc.feePercent,
              ruleCode: calc.ruleCode,
              rateSource: calc.rateSource,
              exchange_timestamp: calc.exchange_timestamp,
              mode: formData.mode,
            }
          : undefined,
      };

      if (!amtSend || !amtReceive) {
        alert("Veuillez recalculer le montant avant de continuer.");
        setLoading(false);
        return;
      }

      const res = await axios.post("/api/transactions/initiate", payload);
      const createdTransaction = res.data?.transaction;

      setTransactionData({
        ...res.data,
        paymentProvider: formData.paymentProvider || "MANUAL",
      });

      try {
        localStorage.removeItem(draftKey);
      } catch (_) {}

      if ((formData.paymentProvider || "MANUAL") === "STRIPE") {
        if (!createdTransaction?.id) {
          throw new Error(
            "La transaction a été créée, mais son identifiant est manquant.",
          );
        }

        const intentResponse = await axios.post(
          `/api/transactions/${createdTransaction.id}/stripe-intent`,
        );

        if (!intentResponse.data?.clientSecret) {
          throw new Error("Stripe n'a pas fourni le secret de paiement.");
        }

        setStripeClientSecret(intentResponse.data.clientSecret);
        setStep(5);
      } else {
        setStep(5);
        setTimeout(() => setStep(6), 1200);
      }

      // } catch (err) {
      //   console.error("Erreur transaction:", err?.response?.data || err.message);
      //   alert(err?.response?.data?.error || "❌ Erreur lors de l'initiation de la transaction.");
      //   setStep(4);
    } catch (err) {
      const data = err?.response?.data;

      console.error("Erreur transaction:", data || err.message);

      if (data?.code === "KYC_REQUIRED") {
        setKycRequired({
          title: "Vérification d'identité requise",
          message:
            data?.error ||
            "Votre vérification d'identité est requise pour toute transaction de 1000 CAD ou plus.",
          redirectTo: data?.redirectTo || "/profile",
        });

        setStep(4);
        return;
      }

      alert(data?.error || "❌ Erreur lors de l'initiation de la transaction.");
      setStep(4);
    } finally {
      setLoading(false);
    }
  };

  const handleNewTransaction = () => {
    try {
      localStorage.removeItem(draftKey);
      setStripeClientSecret("");
    } catch (_) {}

    setFormData(initialFormData());
    setTransactionData(null);
    setStep(1);
  };

  const steps = ["Pays", "Montant", "Expéditeur", "Résumé", "Traitement"];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 font-roboto overflow-x-hidden">
      <div className="flex min-w-0">
        <div className="hidden md:block">
          <Sidebar />
        </div>

        <main className="flex-1 min-w-0 pb-28 md:pb-8">
          <div className="mx-auto max-w-3xl min-w-0 px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                Nouvelle transaction
              </h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Suivez les étapes pour finaliser votre envoi
              </p>
            </div>

            <Stepper steps={steps} current={step} />

            <section className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-5 min-w-0 overflow-hidden">
              {step === 1 && (
                <CountryStep
                  formData={formData}
                  setFormData={setFormData}
                  onNext={nextStep}
                />
              )}

              {step === 2 && (
                <AmountStep
                  formData={formData}
                  setFormData={setFormData}
                  onNext={nextStep}
                  onPrev={prevStep}
                />
              )}

              {step === 3 && (
                <SenderAndBeneficiaryStep
                  formData={formData}
                  setFormData={setFormData}
                  currentUser={currentUser}
                  onNext={nextStep}
                  onPrev={prevStep}
                />
              )}

              {step === 4 && (
                <TransactionSummary
                  formData={formData}
                  setFormData={setFormData}
                  onConfirm={handleConfirm}
                  loading={loading}
                  onPrev={prevStep}
                />
              )}

              {step === 5 &&
                transactionData?.paymentProvider === "STRIPE" &&
                stripeClientSecret && (
                  <Elements
                    stripe={stripePromise}
                    options={{ clientSecret: stripeClientSecret }}
                  >
                    <StripePaymentForm
                      transaction={transactionData.transaction}
                      onPaymentConfirmed={(confirmedTransaction) => {
                        setTransactionData((prev) => ({
                          ...prev,
                          transaction: confirmedTransaction,
                        }));
                        setStep(6);
                      }}
                    />
                  </Elements>
                )}

              {step === 5 &&
                (transactionData?.paymentProvider !== "STRIPE" ||
                  !stripeClientSecret) && <Processing />}

              {step === 6 &&
                transactionData &&
                (transactionData.paymentProvider === "STRIPE" ? (
                  <div className="mx-auto max-w-2xl space-y-4 rounded-2xl border border-emerald-500/30 bg-emerald-50 p-6 dark:bg-emerald-900/20">
                    <h2 className="text-xl font-bold text-emerald-800 dark:text-emerald-200">
                      Paiement confirmé
                    </h2>
                    <p className="text-sm text-slate-700 dark:text-slate-200">
                      Le serveur a confirmé la réception du paiement. Votre
                      transfert reste soumis au processus de validation de NGC
                      Financial.
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      Référence :{" "}
                      {transactionData.transaction?.paymentRef ||
                        transactionData.transactionNumber ||
                        transactionData.transaction?.id}
                    </p>
                    <button
                      type="button"
                      onClick={handleNewTransaction}
                      className="w-full rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white dark:bg-white dark:text-slate-900"
                    >
                      Nouvelle transaction
                    </button>
                  </div>
                ) : (
                  <SuccessCard
                    transactionData={transactionData}
                    direction={direction}
                    onNew={handleNewTransaction}
                  />
                ))}
            </section>
          </div>

          <div className="md:hidden fixed bottom-0 left-0 w-full z-50">
            <BottomNav />
          </div>
        </main>
      </div>
      {kycRequired && (
        <KycRequiredModal
          title={kycRequired.title}
          message={kycRequired.message}
          onClose={() => setKycRequired(null)}
          onGoToProfile={() => {
            setKycRequired(null);
            navigate(kycRequired.redirectTo || "/profile");
          }}
        />
      )}
    </div>
  );
}

function Stepper({ steps, current }) {
  return (
    <>
      <div className="md:hidden rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-3 overflow-hidden">
        <div className="flex items-center justify-between gap-3 min-w-0">
          <div className="text-sm font-semibold text-slate-900 dark:text-white">
            Étape {current} / {steps.length}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 truncate min-w-0">
            {steps[current - 1] || ""}
          </div>
        </div>

        <div className="mt-3 overflow-x-auto">
          <div className="flex gap-2 min-w-max pb-1">
            {steps.map((label, idx) => {
              const stepIndex = idx + 1;
              const active = current === stepIndex;
              const done = current > stepIndex;

              return (
                <div
                  key={label}
                  className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 border whitespace-nowrap
                    ${
                      done || active
                        ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
                        : "bg-transparent text-slate-600 border-black/10 dark:text-slate-300 dark:border-white/10"
                    }`}
                >
                  <span
                    className={`h-6 w-6 rounded-xl flex items-center justify-center text-xs font-bold border
                      ${done || active ? "border-white/20 dark:border-black/10" : "border-black/10 dark:border-white/10"}`}
                  >
                    {stepIndex}
                  </span>
                  <span className="text-sm font-medium">{label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="hidden md:block rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-4">
        <div className="flex items-center justify-between gap-2 min-w-0">
          {steps.map((label, idx) => {
            const stepIndex = idx + 1;
            const active = current === stepIndex;
            const done = current > stepIndex;

            return (
              <div key={label} className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <div
                    className={`h-8 w-8 rounded-xl flex items-center justify-center text-sm font-semibold border transition
                      ${
                        done || active
                          ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
                          : "bg-transparent text-slate-500 border-black/10 dark:text-slate-300 dark:border-white/10"
                      }`}
                  >
                    {stepIndex}
                  </div>

                  <div className="min-w-0">
                    <div
                      className={`text-sm font-medium truncate ${
                        active
                          ? "text-slate-900 dark:text-white"
                          : "text-slate-500 dark:text-slate-300"
                      }`}
                    >
                      {label}
                    </div>
                  </div>
                </div>

                {idx < steps.length - 1 && (
                  <div
                    className={`mt-2 h-1 rounded-full ${done ? "bg-slate-900 dark:bg-white" : "bg-black/5 dark:bg-white/10"}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function SuccessCard({ transactionData, onNew, direction }) {
  const transactionNumber =
    transactionData.transactionNumber ||
    transactionData.transactionRef ||
    transactionData.paymentRef ||
    transactionData?.transaction?.paymentRef ||
    "-";

  const amount =
    transactionData.amount ??
    transactionData?.transaction?.amount ??
    transactionData?.transaction?.amountToSend ??
    transactionData?.calculation?.amountToSend ??
    "-";

  const currency =
    transactionData.currency ??
    transactionData?.transaction?.currency ??
    transactionData?.transaction?.currencyFrom ??
    transactionData?.calculation?.currencyFrom ??
    "XAF";

  const recipientAmount =
    transactionData.recipientAmount ??
    transactionData?.transaction?.convertedAmount ??
    transactionData?.calculation?.convertedAmount ??
    "-";

  const recipientCurrency =
    transactionData.recipientCurrency ??
    transactionData?.transaction?.recipientCurrency ??
    transactionData?.calculation?.recipientCurrency ??
    "XAF";

  const interacEmail = "ngc@mtech-digitals.com";
  const interacPassword = "ngcfinancial";

  const showInterac = Boolean(direction?.isCanadaToAfrica);
  const showAfricaToCanada = Boolean(direction?.isAfricaToCanada);
  const showNigeria = Boolean(
    direction?.isCanadaToNigeria || direction?.isNigeriaToCanada,
  );

  if (showNigeria) {
    return (
      <div className="max-w-2xl mx-auto p-6 sm:p-7 rounded-2xl border border-emerald-500/20 bg-gradient-to-b from-emerald-500/10 to-emerald-500/5 dark:from-emerald-500/10 dark:to-emerald-500/0 shadow-sm">
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-emerald-950 dark:text-emerald-100">
            Transaction enregistrée
          </h2>
          <p className="mt-1 text-sm sm:text-base text-emerald-900/80 dark:text-emerald-200/80">
            Paiement bancaire requis pour le Nigéria.
          </p>
        </div>

        <div className="mt-5 rounded-2xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/5 p-4 space-y-3">
          <p className="text-emerald-950 dark:text-emerald-100">
            Votre numéro de transfert{" "}
            <span className="font-semibold">{transactionNumber}</span> a bien
            été enregistré.
          </p>
          <p className="text-emerald-950 dark:text-emerald-100">
            Montant à payer :{" "}
            <span className="font-bold">
              {amount} {currency}
            </span>
          </p>
          <p className="text-emerald-950 dark:text-emerald-100">
            Une fois le paiement validé, le bénéficiaire recevra{" "}
            <span className="font-bold">
              {recipientAmount} {recipientCurrency}
            </span>
            .
          </p>
          <p className="text-emerald-950 dark:text-emerald-100">
            Vérifiez soigneusement le <strong>Bank Name</strong> et le{" "}
            <strong>Nigerian Account Number</strong> avant validation.
          </p>
        </div>

        <button
          onClick={onNew}
          className="mt-5 w-full px-6 py-3 rounded-2xl bg-slate-900 text-white hover:opacity-95 transition dark:bg-white dark:text-slate-900 font-semibold"
        >
          Nouvelle transaction
        </button>
      </div>
    );
  }

  if (showAfricaToCanada) {
    return (
      <div className="max-w-2xl mx-auto p-6 sm:p-7 rounded-2xl border border-emerald-500/20 bg-gradient-to-b from-emerald-500/10 to-emerald-500/5 dark:from-emerald-500/10 dark:to-emerald-500/0 shadow-sm">
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-emerald-950 dark:text-emerald-100">
            Transaction enregistrée
          </h2>
          <p className="mt-1 text-sm sm:text-base text-emerald-900/80 dark:text-emerald-200/80">
            Veuillez procéder à votre paiement.
          </p>
        </div>

        <div className="mt-5 rounded-2xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/5 p-4">
          <p className="text-emerald-950 dark:text-emerald-100">
            Votre numéro de transfert{" "}
            <span className="font-semibold tabular-nums">
              {transactionNumber}
            </span>{" "}
            a bien été enregistré.
          </p>

          <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-sm text-emerald-900/80 dark:text-emerald-200/80">
              Montant à payer :
            </span>
            <span className="text-lg font-extrabold text-emerald-700 dark:text-emerald-300 tabular-nums">
              {amount} {currency}
            </span>
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/5 p-4">
          <p className="text-emerald-950 dark:text-emerald-100 leading-relaxed mb-3">
            Orange money regulier gaelle ngokseu{" "}
            <span className="font-semibold tabular-nums">655337245</span>.
          </p>

          <p className="text-emerald-950 dark:text-emerald-100 leading-relaxed">
            Pour mtn , Code{" "}
            <span className="font-semibold tabular-nums">
              *126 * 14 * 681388665 *montant#
            </span>
          </p>
        </div>

        <div className="mt-4 rounded-2xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/5 p-4">
          <p className="text-emerald-950 dark:text-emerald-100 leading-relaxed">
            Pour tout dépôt bancaire, contactez-nous :
          </p>

          <div className="mt-2 space-y-2 text-sm text-emerald-900/90 dark:text-emerald-200/90">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-emerald-900/70 dark:text-emerald-200/70">
                Email :
              </span>
              <a
                href="mailto:info@ngcfinancialservices.com"
                className="font-semibold text-emerald-800 dark:text-emerald-200 underline underline-offset-4 break-all"
              >
                info@ngcfinancialservices.com
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-emerald-900/70 dark:text-emerald-200/70">
                WhatsApp :
              </span>
              <span className="font-semibold tabular-nums">
                +237 698 76 62 60
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-emerald-900/70 dark:text-emerald-200/70">
                Ligne sans frais :
              </span>
              <span className="font-semibold tabular-nums">
                +1 800 451 42 10
              </span>
            </div>

            <p className="text-emerald-900/70 dark:text-emerald-200/70">
              Nous vous communiquerons nos coordonnées bancaires.
            </p>
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/5 p-4">
          <p className="text-emerald-950 dark:text-emerald-100 leading-relaxed">
            Une fois le paiement reçu, le bénéficiaire recevra un montant total
            de{" "}
            <span className="font-extrabold text-emerald-700 dark:text-emerald-300 tabular-nums">
              {recipientAmount} CAD
            </span>{" "}
            dans un delai de 48 heures.
          </p>
        </div>

        <button
          onClick={onNew}
          className="mt-5 w-full px-6 py-3 rounded-2xl bg-slate-900 text-white hover:opacity-95 transition dark:bg-white dark:text-slate-900 font-semibold"
        >
          Nouvelle transaction
        </button>
      </div>
    );
  }

  if (showInterac) {
    return (
      <div className="max-w-2xl mx-auto p-6 sm:p-7 rounded-2xl border border-emerald-600/20 bg-emerald-50 dark:bg-emerald-900/20 shadow-sm">
        <h2 className="text-lg sm:text-2xl font-semibold leading-snug mb-4 text-center text-emerald-950 dark:text-emerald-100">
          Transaction enregistrée — veuillez effectuer le virement INTERAC
        </h2>

        <p className="mb-4 text-base leading-relaxed text-emerald-950/90 dark:text-emerald-100/90">
          Votre transfert n°{" "}
          <span className="font-semibold text-emerald-950 dark:text-emerald-100 whitespace-nowrap">
            {transactionNumber}
          </span>{" "}
          a été enregistré avec succès.
        </p>

        <div className="mb-4 rounded-2xl border border-emerald-600/20 bg-white dark:bg-white/5 p-4 sm:p-5 text-emerald-950/90 dark:text-emerald-100/90">
          <p className="mb-3 text-base leading-relaxed">
            Veuillez effectuer un virement{" "}
            <span className="font-semibold">INTERAC</span> de{" "}
            <span className="font-semibold whitespace-nowrap">
              {amount} {currency}
            </span>{" "}
            à l’adresse{" "}
            <span className="font-semibold whitespace-nowrap">
              {interacEmail}
            </span>{" "}
            (dépôt automatique) au nom de{" "}
            <span className="font-semibold">NGC FINANCIAL SERVICES INC</span>.
          </p>

          <p className="mb-3 text-base leading-relaxed">
            Mot de passe (en minuscules) :{" "}
            <span className="font-semibold whitespace-nowrap">
              {interacPassword}
            </span>
            .
          </p>

          <p className="mb-0 text-base leading-relaxed">
            Dès réception du virement, nous procéderons à la validation. Le
            bénéficiaire recevra un montant total de{" "}
            <span className="font-semibold whitespace-nowrap">
              {recipientAmount} {recipientCurrency}
            </span>
            .
          </p>
        </div>

        <p className="mb-4 text-base leading-relaxed text-emerald-950/90 dark:text-emerald-100/90">
          Merci de nous faire confiance.
        </p>

        <div className="mb-6 text-sm leading-relaxed text-emerald-950/80 dark:text-emerald-100/80">
          <p className="mb-2">
            Le paiement <span className="font-semibold">INTERAC</span> est un
            mode de virement qui permet d’envoyer de l’argent directement depuis
            votre compte bancaire.
          </p>
          <p className="mb-0">
            Toute transaction non payée dans un délai de{" "}
            <span className="font-semibold">24 heures</span> sera
            automatiquement annulée par le système.
          </p>
        </div>

        <button
          onClick={onNew}
          className="w-full px-6 py-3 rounded-2xl bg-slate-900 text-white hover:opacity-95 transition dark:bg-white dark:text-slate-900 font-medium"
        >
          Nouvelle transaction
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 sm:p-7 rounded-2xl border border-emerald-500/20 bg-gradient-to-b from-emerald-500/10 to-emerald-500/5 dark:from-emerald-500/10 dark:to-emerald-500/0 shadow-sm">
      <div className="text-center">
        <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-emerald-950 dark:text-emerald-100">
          Transaction enregistrée
        </h2>
        <p className="mt-1 text-sm sm:text-base text-emerald-900/80 dark:text-emerald-200/80">
          Veuillez procéder à votre paiement.
        </p>
      </div>

      <div className="mt-5 rounded-2xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/5 p-4">
        <p className="text-emerald-950 dark:text-emerald-100">
          Votre numéro de transfert{" "}
          <span className="font-semibold tabular-nums">
            {transactionNumber}
          </span>{" "}
          a bien été enregistré.
        </p>

        <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-sm text-emerald-900/80 dark:text-emerald-200/80">
            Montant à payer :
          </span>
          <span className="text-lg font-extrabold text-emerald-700 dark:text-emerald-300 tabular-nums">
            {amount} {currency}
          </span>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/5 p-4">
        <p className="text-emerald-950 dark:text-emerald-100 leading-relaxed">
          Une fois le paiement reçu, le bénéficiaire recevra un montant total de{" "}
          <span className="font-extrabold text-emerald-700 dark:text-emerald-300 tabular-nums">
            {recipientAmount} {recipientCurrency}
          </span>{" "}
          sous 48 heures.
        </p>
      </div>

      <button
        onClick={onNew}
        className="mt-5 w-full px-6 py-3 rounded-2xl bg-slate-900 text-white hover:opacity-95 transition dark:bg-white dark:text-slate-900 font-semibold"
      >
        Nouvelle transaction
      </button>
    </div>
  );
}
function KycRequiredModal({ title, message, onClose, onGoToProfile }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 shadow-xl border border-black/10 dark:border-white/10 p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
            ⚠️
          </div>

          <div className="min-w-0">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
              {title || "KYC requis"}
            </h3>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {message}
            </p>

            <p className="mt-3 text-xs leading-5 text-slate-500 dark:text-slate-400">
              Complétez vos informations d’identité et téléversez votre document
              afin que l’équipe puisse vérifier votre profil.
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-black/10 dark:border-white/10 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-black/5 dark:hover:bg-white/10"
          >
            Retour au résumé
          </button>

          <button
            type="button"
            onClick={onGoToProfile}
            className="rounded-2xl bg-slate-900 dark:bg-white px-4 py-2.5 text-sm font-semibold text-white dark:text-slate-900 hover:opacity-90"
          >
            Compléter mon KYC
          </button>
        </div>
      </div>
    </div>
  );
}
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
